import React, { useEffect, useRef, useState } from 'react';
import { createChart, LineSeries } from 'lightweight-charts';

const EquityCurveInline = ({ data }) => {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const [barSpacing, setBarSpacing] = useState(6); // default spacing

  useEffect(() => {
    if (!containerRef.current) return;
    // destroy existing
    if (chartRef.current) {
      chartRef.current.remove();
      chartRef.current = null;
      seriesRef.current = null;
    }
    if (!Array.isArray(data) || data.length === 0) return;
    const chart = createChart(containerRef.current, {
      width: containerRef.current.clientWidth,
      height: 220,
      layout: { background: { type: 'solid', color: 'transparent' }, textColor: getComputedStyle(document.documentElement).getPropertyValue('--color-text-primary')?.trim() || '#fff' },
      grid: { vertLines: { color: 'rgba(100,116,139,0.2)' }, horzLines: { color: 'rgba(100,116,139,0.2)' } },
      rightPriceScale: { borderColor: 'rgba(100,116,139,0.35)' },
      timeScale: { borderColor: 'rgba(100,116,139,0.35)', barSpacing, fixLeftEdge: false, fixRightEdge: false },
    });
    const series = chart.addSeries(LineSeries, { color: '#0ea5e9', lineWidth: 2 });
    chartRef.current = chart;
    seriesRef.current = series;
    // Normalize raw data into seconds epoch and numeric equity
    const normalizePoint = (p) => {
      const rawT = p.timestamp ?? p.time ?? p.date;
      let ts;
      if (rawT instanceof Date) {
        ts = Math.floor(rawT.getTime() / 1000);
      } else if (typeof rawT === 'number') {
        // If looks like ms, convert; if already seconds keep
        ts = rawT > 1000000000000 ? Math.floor(rawT / 1000) : Math.floor(rawT);
      } else if (typeof rawT === 'string') {
        const parsed = Date.parse(rawT);
        if (!isNaN(parsed)) ts = Math.floor(parsed / 1000);
      }
      const valRaw = p.equity ?? p.value ?? p.balance;
      const value = typeof valRaw === 'number' ? valRaw : (parseFloat(valRaw) || 0);
      return { time: ts, value };
    };

    // Map & filter invalid
    const mappedInitial = data.map(normalizePoint).filter(d => typeof d.time === 'number' && isFinite(d.time) && isFinite(d.value));
    // Sort ascending
    mappedInitial.sort((a,b) => a.time - b.time);
    // Deduplicate same timestamps keeping the last value (post-sort means overwrite)
    const dedup = new Map();
    for (const pt of mappedInitial) dedup.set(pt.time, pt.value);
    let mapped = Array.from(dedup.entries()).map(([time, value]) => ({ time, value }));
    // Ensure strictly increasing order (after dedupe it already is, but guard anyway)
    mapped.sort((a,b) => a.time - b.time);
    let lastTime = -Infinity;
    const strict = [];
    for (const pt of mapped) {
      if (pt.time > lastTime) { strict.push(pt); lastTime = pt.time; }
      // If equal or lower, skip (shouldn't happen after dedupe)
    }
    if (strict.length < mappedInitial.length && typeof window !== 'undefined') {
      // Optional debug (can be removed later)
      // console.debug('EquityCurveInline: pruned duplicate / non-increasing timestamps', mappedInitial.length - strict.length);
    }
    series.setData(strict);
    // Fit full content initially
    chart.timeScale().fitContent();
    const handleResize = () => {
      if (!containerRef.current || !chartRef.current) return;
      chartRef.current.resize(containerRef.current.clientWidth, 220);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
        seriesRef.current = null;
      }
    };
  }, [data]); // only recreate when data changes, not when barSpacing changes

  // Update bar spacing live without recreating if only spacing changed
  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.applyOptions({ timeScale: { barSpacing } });
    }
  }, [barSpacing]);

  const handleMaxZoomOut = () => {
    setBarSpacing(0.1); // tighter spacing
    if (chartRef.current) chartRef.current.timeScale().fitContent();
  };

  const handleUltraZoomOut = () => {
    setBarSpacing(0.01); // extreme compression
    if (chartRef.current) chartRef.current.timeScale().fitContent();
  };

  const handleFit = () => {
    if (chartRef.current) chartRef.current.timeScale().fitContent();
  };

  const handleResetSpacing = () => {
    setBarSpacing(6);
    handleFit();
  };

  return (
    <div style={{ width: '100%', height: 220, position: 'relative' }}>
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
      <div
        style={{
          position: 'absolute',
          top: 4,
          right: 4,
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          background: 'rgba(15,23,42,0.6)',
          padding: '4px 8px',
          borderRadius: 6,
          backdropFilter: 'blur(4px)',
          zIndex: 50,
          pointerEvents: 'auto'
        }}
      >
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10 }}>
          <span>Spacing</span>
          <input
            type="range"
            min={0.01}
            max={14}
            step={0.01}
            value={barSpacing}
            onChange={(e) => setBarSpacing(parseFloat(e.target.value))}
            style={{ width: 90 }}
          />
        </label>
        <button onClick={handleMaxZoomOut} style={{ fontSize: 10, padding: '2px 6px', background: '#0ea5e9', color: '#fff', borderRadius: 4 }}>Max</button>
        <button onClick={handleUltraZoomOut} style={{ fontSize: 10, padding: '2px 6px', background: '#0369a1', color: '#fff', borderRadius: 4 }}>Ultra</button>
        <button onClick={handleFit} style={{ fontSize: 10, padding: '2px 6px', background: '#334155', color: '#fff', borderRadius: 4 }}>Fit</button>
        <button onClick={handleResetSpacing} style={{ fontSize: 10, padding: '2px 6px', background: '#475569', color: '#fff', borderRadius: 4 }}>Reset</button>
      </div>
    </div>
  );
};

export default EquityCurveInline;
